import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Observable, Subject, takeUntil } from 'rxjs';
import { FormInputComponent, NotificationService, SharedModule, StringCustomFieldConfig } from '@vendure/admin-ui/core';
import { DEFAULT_ACTIVE_LANGUAGE } from '../constants';
import { Format, TextValue } from '../ui-types';
import { safeJSONParse } from '../utils';

const FORBID_TAGS = ['script', 'iframe', 'object', 'embed', 'form', 'input', 'button'];

@Component({
  selector: 'text-value-input',
  standalone: true,
  imports: [SharedModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <vdr-card>
      <div class="clr-row mb-2" *ngIf="!lockedFormat">
        <vdr-form-field class="clr-col-4" [label]="'Format'">
          <select [formControl]="formatCtrl" [disabled]="readonly">
            <option value="plain">Plain Text</option>
            <option value="rich">Rich Text Editor</option>
            <option value="html">Custom HTML</option>
          </select>
        </vdr-form-field>
      </div>

      <ng-container [ngSwitch]="formatCtrl.value">
        <vdr-form-field *ngSwitchCase="'rich'" [label]="'Content'">
          <vdr-rich-text-editor [formControl]="valueCtrl"></vdr-rich-text-editor>
        </vdr-form-field>

        <vdr-form-field *ngSwitchCase="'html'" [label]="'Content (unsafe HTML is sanitized)'">
          <textarea rows="6" [formControl]="valueCtrl" (blur)="onHtmlBlur()"></textarea>
        </vdr-form-field>

        <vdr-form-field *ngSwitchCase="'plain'" [label]="'Content'">
          <textarea rows="4" [formControl]="valueCtrl"></textarea>
        </vdr-form-field>
      </ng-container>
    </vdr-card>
  `,
})
export class TextValueInputComponent implements FormInputComponent<StringCustomFieldConfig>, OnInit, AfterViewInit, OnDestroy {
  @Input() readonly!: boolean;
  @Input() formControl!: FormControl<string>;
  @Input() config!: StringCustomFieldConfig;

  activeLang = DEFAULT_ACTIVE_LANGUAGE;
  formatCtrl = new FormControl<Format>('plain', { nonNullable: true });
  valueCtrl = new FormControl<string>('', { nonNullable: true });
  private destroyed$ = new Subject<void>();

  private model: TextValue = { format: 'plain', i18n: {} };
  lockedFormat: Format | null = null;
  private languageChanges$?: Observable<unknown>;

  constructor(private changeDetector: ChangeDetectorRef, private notificationService: NotificationService) {}

  ngOnInit(): void {
    const args = (this.config as any)?.ui?.args ?? {};
    if (typeof args.activeLanguageCode === 'string' && args.activeLanguageCode) this.activeLang = args.activeLanguageCode;
    if (typeof args.lockedFormat === 'string' && ['plain', 'rich', 'html'].includes(args.lockedFormat)) this.lockedFormat = args.lockedFormat as Format;
    if (args?.languageChanges$ && typeof args.languageChanges$.subscribe === 'function') {
      this.languageChanges$ = args.languageChanges$ as Observable<unknown>;
    }

    this.applyFromJson(this.formControl?.value ?? null);
    this.formControl?.valueChanges?.pipe(takeUntil(this.destroyed$)).subscribe(val => this.applyFromJson(val));
    this.formatCtrl.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(() => this.flushToParent());
    this.valueCtrl.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(() => this.flushToParent());

    this.languageChanges$?.pipe(takeUntil(this.destroyed$)).subscribe(event => {
      const nextLanguage = typeof event === 'string' ? event : (event as any)?.code;
      if (!nextLanguage || nextLanguage === this.activeLang) return;
      this.activeLang = nextLanguage;
      this.valueCtrl.setValue(this.model.i18n?.[this.activeLang] ?? '', { emitEvent: false });
      this.changeDetector.markForCheck();
    });
  }

  ngAfterViewInit(): void { this.changeDetector.markForCheck(); }
  ngOnDestroy(): void { this.destroyed$.next(); this.destroyed$.complete(); }

  private applyFromJson(json: string | null): void {
    const fallback: TextValue = { format: this.lockedFormat ?? 'plain', i18n: {} };
    const parsed = safeJSONParse<TextValue>(json ?? '', this.notificationService, fallback) || fallback;
    this.model = parsed as TextValue;
    const normalizedFormat = this.lockedFormat ?? this.model.format ?? 'plain';
    this.model.format = normalizedFormat;
    this.formatCtrl.setValue(normalizedFormat, { emitEvent: false });
    this.valueCtrl.setValue(this.model.i18n?.[this.activeLang] ?? '', { emitEvent: false });
    this.changeDetector.markForCheck();
  }

  onHtmlBlur(): void {
    if (this.formatCtrl.value !== 'html') return;
    const { clean, wasModified } = this.sanitizeHtml(this.valueCtrl.value || '');
    if (wasModified) {
      this.valueCtrl.setValue(clean, { emitEvent: false });
      this.notificationService.warning('Some potentially unsafe content was removed.');
      this.flushToParent();
    }
  }

  private sanitizeHtml(input: string): { clean: string; wasModified: boolean } {
    const tmp = document.createElement('div');
    tmp.innerHTML = input;
    FORBID_TAGS.forEach(tag => tmp.querySelectorAll(tag).forEach(el => el.remove()));
    tmp.querySelectorAll('*').forEach(el => {
      Array.from(el.attributes).forEach(attr => {
        if (attr.name.toLowerCase().startsWith('on')) {
          el.removeAttribute(attr.name);
        }
      });
    });
    const clean = tmp.innerHTML;
    return { clean, wasModified: clean !== input };
  }

  private flushToParent(): void {
    const format = this.formatCtrl.value;
    let value = this.valueCtrl.value ?? '';
    if (format === 'html') value = this.sanitizeHtml(value).clean;

    const next: TextValue = { format, i18n: { ...(this.model.i18n ?? {}), [this.activeLang]: value } };
    this.model = next;
    const json = JSON.stringify(next);
    if (this.formControl?.value !== json) {
      this.formControl.setValue(json);
      this.formControl.markAsDirty();
    }
  }
}
